import React, { useState } from "react";
import { FiZap, FiAlertTriangle } from "react-icons/fi";
import { HealthRing, MiniBar, StatusBadge } from "./waterHelpers";

const healthColor = (v) => v >= 80 ? "#10b981" : v >= 60 ? "#f59e0b" : "#ef4444";

const WmPumpPanel = ({ d }) => {
  const [selected, setSelected] = useState(null);
  const sel = selected ? d.pumps.find(p => p.id === selected) : null;

  const totalPower = d.pumps.filter(p => p.status === "Running").reduce((a, p) => a + p.powerKw, 0);

  return (
    <div className="wm-pumps">

      {/* ── Summary ── */}
      <div className="wp-summary-row">
        <div className="wp-sum-chip" style={{ background: "#d1fae5" }}>
          <FiZap size={14} color="#059669" />
          <span style={{ color: "#059669" }}>{d.pumps.filter(p => p.status === "Running").length} Running</span>
        </div>
        <div className="wp-sum-chip" style={{ background: "#fef3c7" }}>
          <FiZap size={14} color="#d97706" />
          <span style={{ color: "#d97706" }}>{d.pumps.filter(p => p.status === "Standby").length} Standby</span>
        </div>
        <div className="wp-sum-chip" style={{ background: "#fee2e2" }}>
          <FiAlertTriangle size={14} color="#dc2626" />
          <span style={{ color: "#dc2626" }}>{d.pumps.filter(p => p.status === "Offline").length} Offline</span>
        </div>
        <div className="wp-sum-chip" style={{ background: "#dbeafe" }}>
          <FiZap size={14} color="#2563eb" />
          <span style={{ color: "#2563eb" }}>{totalPower.toFixed(1)} kW Total Load</span>
        </div>
      </div>

      <div className="wp-layout">

        {/* ── Pump Cards ── */}
        <div className="wp-pump-list">
          {d.pumps.map(pump => {
            const hc = healthColor(pump.healthScore);
            const isActive = selected === pump.id;
            return (
              <div
                key={pump.id}
                className={`wp-pump-card ${isActive ? "wp-pump-active" : ""}`}
                onClick={() => setSelected(isActive ? null : pump.id)}
              >
                <div className="wp-pump-header">
                  <div className="wp-pump-ring">
                    <HealthRing value={pump.healthScore} size={52} stroke={6} color={hc} />
                    <div className="wp-pump-score" style={{ color: hc }}>{pump.healthScore}</div>
                  </div>
                  <div className="wp-pump-info">
                    <div className="wp-pump-name">{pump.name}</div>
                    <div className="wp-pump-loc">{pump.location}</div>
                    <div style={{ display: "flex", gap: "0.4rem", marginTop: 3 }}>
                      <StatusBadge status={pump.status} />
                      <span style={{ fontSize: "0.7rem", color: "#94a3b8" }}>{pump.type}</span>
                    </div>
                  </div>
                </div>

                <div className="wp-pump-stats">
                  <div className="wp-ps-item"><span>Power</span><strong>{pump.powerKw} kW</strong></div>
                  <div className="wp-ps-item"><span>Flow</span><strong>{pump.flowLpm > 0 ? `${pump.flowLpm} L/min` : "—"}</strong></div>
                  <div className="wp-ps-item"><span>Hours Today</span><strong>{pump.runningHoursToday}h</strong></div>
                  <div className="wp-ps-item"><span>Efficiency</span><strong style={{ color: pump.efficiency > 80 ? "#10b981" : "#f59e0b" }}>{pump.efficiency > 0 ? `${pump.efficiency}%` : "—"}</strong></div>
                </div>

                <MiniBar value={pump.healthScore} color={hc} height={4} />

                {pump.alerts.length > 0 && (
                  <div className="wp-pump-alert"><FiAlertTriangle size={11} /> {pump.alerts[0]}</div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Detail Panel ── */}
        <div className="wp-detail">
          {sel ? (
            <>
              <div className="wp-detail-title">{sel.name}</div>
              <div className="wp-detail-sub">{sel.location} · {sel.type}</div>

              <div className="wp-detail-health">
                <HealthRing value={sel.healthScore} size={90} stroke={10} color={healthColor(sel.healthScore)} />
                <div className="wp-dh-center" style={{ color: healthColor(sel.healthScore) }}>
                  {sel.healthScore}
                  <span style={{ fontSize: 10, color: "#64748b", fontWeight: 400 }}>Health</span>
                </div>
              </div>

              <div className="wp-detail-grid">
                {[
                  ["Status",          sel.status],
                  ["Power",           `${sel.powerKw} kW`],
                  ["Current",         sel.currentAmps > 0 ? `${sel.currentAmps} A` : "—"],
                  ["Voltage",         sel.voltageV > 0 ? `${sel.voltageV} V` : "—"],
                  ["Flow Rate",       sel.flowLpm > 0 ? `${sel.flowLpm} L/min` : "—"],
                  ["Pressure",        sel.pressureBar > 0 ? `${sel.pressureBar} bar` : "—"],
                  ["Vibration",       sel.vibration],
                  ["Temp",            sel.temperature > 0 ? `${sel.temperature}°C` : "—"],
                  ["Efficiency",      sel.efficiency > 0 ? `${sel.efficiency}%` : "—"],
                  ["Run Today",       `${sel.runningHoursToday}h`],
                  ["Total Hours",     `${sel.totalRunningHours.toLocaleString()}h`],
                  ["Last Service",    sel.lastMaintenance],
                  ["Next Service",    sel.nextMaintenance],
                ].map(([label, val]) => (
                  <div key={label} className="wp-dg-item">
                    <span className="wp-dg-label">{label}</span>
                    <span className="wp-dg-val"
                      style={{ color: label === "Next Service" && sel.nextMaintenance.includes("Overdue") ? "#ef4444" : undefined }}>
                      {val}
                    </span>
                  </div>
                ))}
              </div>

              {sel.alerts.length > 0 && (
                <div className="wp-detail-alerts">
                  {sel.alerts.map((a, i) => (
                    <div key={i} className="wp-da-row"><FiAlertTriangle size={13} color="#ef4444" /> {a}</div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="wp-empty">
              <FiZap size={40} color="#cbd5e1" />
              <p>Select a pump to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WmPumpPanel;
